import React from "react";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useLanguage from "../../hooks/useLanguage.js";
import {
  trackCTA,
  trackInvitationPreviewViewed,
  useSectionOnceObserver,
} from "../../lib/analytics";
import { buildDemoInvitationUrl } from "../../lib/demoInvitation.js";

/**
 * "A real invitation, open right now."
 *
 * The page's strongest asset is not an image of the product — it is the
 * product, open, at a URL anyone can visit. So this gets a section of its own
 * rather than a line in the FAQ, and it is deliberately the only section on the
 * page with a framed, centred treatment: on a page of left-aligned editorial
 * blocks, the one thing that is an offer rather than a statement should look
 * different from everything around it.
 *
 * No photograph here on purpose. A picture of a couple next to "here is the
 * real thing" competes with the link for the same attention, and the section
 * is doing better work as a clean invitation to leave the page.
 *
 * The impression reuses the EXISTING `invitation_preview` section name via
 * `trackInvitationPreviewViewed`, the same one the homepage pricing card fires
 * for the same offer — so the two surfaces are directly comparable instead of
 * splitting one funnel across two names.
 */
const InvitationDemo = () => {
  const { t } = useTranslation();
  const { shortLanguage } = useLanguage();
  const analyticsContext = useAnalyticsContext();

  const sectionRef = useSectionOnceObserver(() =>
    trackInvitationPreviewViewed(analyticsContext),
  );

  return (
    <section ref={sectionRef} className="section topless wps-section">
      <div className="container">
        <div className="dwi-demo">
          <p className="wps-eyebrow">
            {t("digitalWeddingInvitation.demo.eyebrow")}
          </p>
          <h2 className="heading-50">
            {t("digitalWeddingInvitation.demo.heading")}
          </h2>
          <p className="body-20 dwi-demo-body">
            {t("digitalWeddingInvitation.demo.body")}
          </p>
          <div className="dwi-demo-action">
            <Button
              href={buildDemoInvitationUrl(shortLanguage)}
              target="_blank"
              text={t("digitalWeddingInvitation.demo.cta")}
              className="button-lg"
              onClick={() =>
                trackCTA("digital_wedding_invitation_demo", {
                  ...analyticsContext,
                  ctaName: "view_demo",
                })
              }
            />
          </div>
          <p className="wps-note dwi-demo-note">
            {t("digitalWeddingInvitation.demo.note")}
          </p>
        </div>
      </div>
    </section>
  );
};

export default InvitationDemo;
